import { AuthenticationStore } from "@istari-frontend/auth";
import { useExperimentalFeature } from "@istari-frontend/experimental-features";
import { getEnv } from "@istari-frontend/shared";
import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useAuth } from "react-oidc-context";
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, useLocation } from "react-router";

import { AiChatFloatingWidget } from "~/ai-chat/feature/ai-chat-floating-widget";
import { AiChatProvider } from "~/ai-chat/feature/use-ai-chat";
import { ChatViewContextProvider } from "~/ai-chat/feature/use-chat-view-context";
import { InfosecBanner } from "~/control-tags/feature/infosec-banner";
import { RequireSession, SessionProvider, surfaceEntries } from "~/session";

import App from "./App";
import ErrorBoundaryDisplay from "./components/ErrorBoundaryDisplay";
import { ItarBanner } from "./components/itar-banner";
import { TopBannerOffsetEffect, useTopBannerOffset } from "./components/top-banner-offset";
import { NotificationToastHost } from "./notifications/feature/notification-toast-host";
import { LegacyArtifactRedirect } from "./pages/Main/Artifact/legacy-artifact-redirect";
import { systemHomeTabElements } from "./pages/Main/SystemHome/system-home-tab-elements";
import { AdminIndexRedirect } from "./router/admin-index-redirect";
import {
  ConnectedResourceGuard,
  ResourcesDashboardGuard,
  SystemsDashboardGuard,
} from "./router/dashboard-experimental-guards";
import { LegacyFilesRedirect } from "./router/legacy-files-redirect";
import { RootRedirect } from "./router/root-redirect";
import { TenantUsersRedirect } from "./router/tenant-users-redirect";
import { routes } from "./routes";
import { SystemEditGuard } from "./systems/feature/system-edit-guard";
import { createSystemHomeTabRoutes } from "./systems/feature/system-home-tab-routes";
import { UploadProvider } from "./upload/upload-provider";

const SystemHomePage = React.lazy(() => import("./pages/Main/SystemHome"));

const systemHomeTabRoutes = createSystemHomeTabRoutes(systemHomeTabElements);

const SignIn = React.lazy(() => import("./pages/Authentication/SignIn"));

const AllSystems = React.lazy(() => import("./pages/Main/AllSystems"));
const SystemsDashboard = React.lazy(() => import("./pages/Main/SystemsDashboard"));
const AiChatPage = React.lazy(() => import("~/ai-chat/feature/ai-chat-page"));

const AllModelsNew = React.lazy(() => import("./pages/Main/AllModelsNew/all-models-new"));
const ResourcesDashboard = React.lazy(() => import("./pages/Main/ResourcesDashboard"));
const Jobs = React.lazy(() => import("./pages/Main/Jobs"));
const Settings = React.lazy(() => import("./pages/Main/Settings"));

const Comment = React.lazy(() => import("./pages/Main/CommentPage"));
const SystemDetailsPage = React.lazy(() => import("./pages/Main/System"));
const FunctionAuthCallback = React.lazy(() => import("./pages/Main/FunctionAuthCallback"));

const AdminPageLayout = React.lazy(() => import("./pages/Main/Admin/admin-page-layout"));
const ConsolePageLayout = React.lazy(() => import("./pages/Main/Console/console-page-layout"));
const AdminUsersPage = React.lazy(() => import("./pages/Main/Admin/users-page"));
const AdminCompatibilityPage = React.lazy(() => import("./pages/Main/Admin/compatibility-page"));
const AdminControlTagsPage = React.lazy(() => import("./pages/Main/Admin/control-tags-page"));
const AdminInfosecPage = React.lazy(() => import("./pages/Main/Admin/infosec-page"));
const AdminIntegrationsPage = React.lazy(() => import("./pages/Main/Admin/integrations-page"));
const AdminAiSettingsPage = React.lazy(() => import("./pages/Main/Admin/ai-settings-page"));
const AdminAiModelsPage = React.lazy(() => import("./pages/Main/Admin/ai-models-page"));
const AdminUsagePage = React.lazy(() => import("./pages/Main/Admin/usage-page"));
const AdminSecureConnectionsPage = React.lazy(() => import("./pages/Main/Admin/secure-connections-page"));
const AdminSecureConnectionSettingsPage = React.lazy(
  () => import("./pages/Main/Admin/secure-connection-settings-page"),
);
const AdminSecureConnectionSyncStatusPage = React.lazy(
  () => import("./pages/Main/Admin/secure-connection-sync-status-page"),
);
const AdminUserPage = React.lazy(() => import("./pages/Main/Admin/user-page"));
const ConsoleUsersPage = React.lazy(() => import("./pages/Main/Console/console-users-page"));
const ConsoleUserPage = React.lazy(() => import("./pages/Main/Console/console-user-page"));
const ConsoleTenantsPage = React.lazy(() => import("./pages/Main/Console/console-tenants-page"));
const ConsoleTenantPage = React.lazy(() => import("./pages/Main/Console/console-tenant-page"));

// The page each admin registry entry renders; the registry itself stays free of page imports.
const adminPages: Record<string, React.ReactNode> = {
  users: <AdminUsersPage />,
  "control-tags": <AdminControlTagsPage />,
  infosec: <AdminInfosecPage />,
  integrations: <AdminIntegrationsPage />,
  "secure-connections": <AdminSecureConnectionsPage />,
  usage: <AdminUsagePage />,
  compatibility: <AdminCompatibilityPage />,
  "ai-settings": <AdminAiSettingsPage />,
  "ai-models": <AdminAiModelsPage />,
  user: <AdminUserPage />,
};
// The page each console registry entry renders; the registry itself stays free of page imports.
const consolePages: Record<string, React.ReactNode> = {
  tenants: <ConsoleTenantsPage />,
  tenant: <ConsoleTenantPage />,
  "console-users": <ConsoleUsersPage />,
  "console-user": <ConsoleUserPage />,
};
const AgentManagementPage = React.lazy(() => import("./pages/Main/AgentManagement/agent-management-page"));
const CreateChangeRequestPage = React.lazy(() => import("./pages/Main/ChangeRequest/create-change-request-page"));
const ChangeRequestDetailPage = React.lazy(() => import("./pages/Main/ChangeRequest/change-request-detail-page"));
const NotificationsPage = React.lazy(() => import("./pages/Main/Notifications"));

const SearchResultsPage = React.lazy(() => import("./search/feature"));
const PageNotFound = React.lazy(() => import("./components/PageNotFound"));

const LineageDevPage = import.meta.env.DEV ? React.lazy(() => import("./pages/Dev/lineage-dev-page")) : null;

function StackedTopBanners() {
  const { infosecBannerOffset } = useTopBannerOffset();
  return (
    <>
      <TopBannerOffsetEffect />
      <InfosecBanner />
      <ItarBanner topOffset={infosecBannerOffset} />
    </>
  );
}

export function UserMemPagesLayout() {
  const { enabled: isAiChatEnabled } = useExperimentalFeature("ai-chat");

  const content = (
    <SessionProvider>
      <StackedTopBanners />
      <NotificationToastHost />
      <UploadProvider>
        <Outlet />
      </UploadProvider>
      {isAiChatEnabled && <AiChatFloatingWidget />}
    </SessionProvider>
  );

  if (!isAiChatEnabled) {
    return content;
  }

  return (
    <AiChatProvider>
      <ChatViewContextProvider>{content}</ChatViewContextProvider>
    </AiChatProvider>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryDisplay}>
      <App />
      {/* Keyed by location.key so independent history entries (incl. back/forward) restore their own scroll. */}
      <ScrollRestoration getKey={(location) => location.key} />
    </ErrorBoundary>
  );
}

function AuthManager() {
  const location = useLocation();
  const auth = useAuth();
  const AS = AuthenticationStore();
  const redirectPath = AS.consumeRedirectPath();

  return auth.isAuthenticated ? (
    <>{redirectPath ? <Navigate to={redirectPath} replace /> : <Outlet />}</>
  ) : (
    <Navigate to={routes.SignIn} state={{ from: location }} replace />
  );
}

const PageTitle = ({ title }: { title: string }) => {
  const location = useLocation();
  useEffect(() => {
    const baseTitle = "Istari Digital";
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }, [location.pathname, title]);
  return null;
};

const pageNotFoundElement = (
  <>
    <PageTitle title="Page Not Found" />
    <PageNotFound />
  </>
);

export default PageTitle;

// Every resource route renders the same page; only the path it was reached by differs.
const resourcesPage = (
  <ConnectedResourceGuard>
    <PageTitle title="Resources" />
    <AllModelsNew />
  </ConnectedResourceGuard>
);

export const router = createBrowserRouter(
  [
    {
      path: routes.FunctionAuthCallback,
      element: (
        <>
          <PageTitle title="Function Authentication" />
          <FunctionAuthCallback />
        </>
      ),
    },
    {
      path: routes.Root,
      element: <AppWithErrorBoundary />,
      children: [
        {
          path: routes.SignIn,
          element: (
            <>
              <PageTitle title="Sign In" />
              <SignIn />
            </>
          ),
        },
        {
          element: <AuthManager />,
          children: [
            {
              element: <UserMemPagesLayout />,
              children: [
                {
                  path: "/",
                  element: <RootRedirect />,
                },
                {
                  path: "/home",
                  element: <RootRedirect replace />,
                },
                {
                  path: routes.Systems,
                  element: (
                    <>
                      <PageTitle title="Systems" />
                      <AllSystems />
                    </>
                  ),
                },
                {
                  path: routes.SystemsDashboard,
                  element: (
                    <SystemsDashboardGuard>
                      <PageTitle title="Systems" />
                      <SystemsDashboard />
                    </SystemsDashboardGuard>
                  ),
                },
                {
                  path: routes.AiChat,
                  element: (
                    <>
                      <PageTitle title="Istari AI Chat" />
                      <AiChatPage />
                    </>
                  ),
                },
                {
                  path: routes.SystemHome,
                  element: (
                    <SystemsDashboardGuard>
                      <PageTitle title="System" />
                      <SystemHomePage />
                    </SystemsDashboardGuard>
                  ),
                  children: [
                    ...systemHomeTabRoutes,
                    {
                      path: routes.SystemHomeBranchScope,
                      children: systemHomeTabRoutes,
                    },
                  ],
                },
                {
                  path: routes.ResourcesDashboard,
                  element: (
                    <ResourcesDashboardGuard>
                      <PageTitle title="Resources" />
                      <ResourcesDashboard />
                    </ResourcesDashboardGuard>
                  ),
                },
                {
                  path: routes.Resources,
                  element: resourcesPage,
                },
                {
                  path: `${routes.Resources}/:modelId/:revisionId?`,
                  element: resourcesPage,
                },
                {
                  path: `${routes.Resources}/:modelId/:revisionId?/artifact/:artifactId/:artifactRevisionId?`,
                  element: resourcesPage,
                },
                {
                  path: routes.ResourcesUnparentedArtifact,
                  element: resourcesPage,
                },
                {
                  path: "/files/*",
                  element: <LegacyFilesRedirect />,
                },
                {
                  path: routes.FilesConnectedResource,
                  element: resourcesPage,
                },
                {
                  path: routes.Jobs,
                  element: (
                    <>
                      <PageTitle title="Jobs" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.JobDetails,
                  element: (
                    <>
                      <PageTitle title="Job Details" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.Search,
                  element: (
                    <>
                      <PageTitle title="Search" />
                      <SearchResultsPage />
                    </>
                  ),
                },
                {
                  path: routes.Admin,
                  element: (
                    <>
                      <PageTitle title="Admin" />
                      <AdminPageLayout />
                    </>
                  ),
                  children: [
                    {
                      index: true,
                      element: <AdminIndexRedirect />,
                    },
                    ...surfaceEntries("tenant").map((entry) => ({
                      path: entry.path,
                      element: (
                        <RequireSession guards={entry.guards}>
                          <PageTitle title={entry.title} />
                          {adminPages[entry.id]}
                        </RequireSession>
                      ),
                    })),
                    {
                      path: routes.AdminSecureConnectionSettings,
                      element: (
                        <>
                          <PageTitle title="Connection Settings" />
                          <AdminSecureConnectionSettingsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminSecureConnectionSyncStatus,
                      element: (
                        <>
                          <PageTitle title="Sync Status" />
                          <AdminSecureConnectionSyncStatusPage />
                        </>
                      ),
                    },
                  ],
                },
                {
                  path: routes.Console,
                  element: (
                    <>
                      <PageTitle title="Platform Admin Console" />
                      <ConsolePageLayout />
                    </>
                  ),
                  children: [
                    {
                      index: true,
                      element: <Navigate to={routes.ConsoleTenants} replace />,
                    },
                    { path: routes.ConsoleTenantUsers, element: <TenantUsersRedirect /> },
                    ...surfaceEntries("console").map((entry) => ({
                      path: entry.path,
                      element: (
                        <RequireSession guards={entry.guards}>
                          <PageTitle title={entry.title} />
                          {consolePages[entry.id]}
                        </RequireSession>
                      ),
                    })),
                  ],
                },
                {
                  path: "/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agents",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: routes.Agents,
                  element: (
                    <>
                      <PageTitle title="Agents" />
                      <AgentManagementPage />
                    </>
                  ),
                },
                {
                  path: routes.Notifications,
                  element: (
                    <>
                      <PageTitle title="Notifications" />
                      <NotificationsPage />
                    </>
                  ),
                },
                {
                  path: routes.Settings,
                  element: (
                    <>
                      <PageTitle title="Settings" />
                      <Settings />
                    </>
                  ),
                },
                {
                  path: routes.CreateChangeRequest,
                  element: (
                    <SystemEditGuard message="You need edit access to create a change request.">
                      <PageTitle title="New Change Request" />
                      <CreateChangeRequestPage />
                    </SystemEditGuard>
                  ),
                },
                {
                  path: routes.ChangeRequestDetail,
                  element: (
                    <>
                      <PageTitle title="Change Request" />
                      <ChangeRequestDetailPage />
                    </>
                  ),
                },
                {
                  path: routes.SystemDetails,
                  element: (
                    <>
                      <PageTitle title="System Details" />
                      <SystemDetailsPage />
                    </>
                  ),
                },
                {
                  path: routes.Artifact,
                  element: <LegacyArtifactRedirect />,
                },
                {
                  path: routes.CommentDetails,
                  element: (
                    <>
                      <PageTitle title="Comment Details" />
                      <Comment />
                    </>
                  ),
                },
                ...(import.meta.env.DEV && LineageDevPage
                  ? [{ path: "/dev/lineage", element: <LineageDevPage /> }]
                  : []),
                { path: routes.NotFound, element: pageNotFoundElement },
                { path: "*", element: pageNotFoundElement },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    basename: getEnv("BASE_URL"),
  },
);
